import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { cnb } from "cnbuilder";

/**
 * styles are defined in src/styles/default/modernized/modernized-button.module.css
 *
 * This component is a simplified version of the Livongo PulseSDK button which can't
 * be used directly for a variety of technical reasons (styling issues, artifactory access).
 *
 * Left and right icon support has been removed because it's not currently necessary.
 * Support for the 'loading' spinner has also been removed because it was only ever used as an icon.
 */

// eslint-disable-next-line react/display-name
const Button = forwardRef(
  (
    {
      className,
      testAttr,
      children,
      type,
      variant,
      size,
      block,
      disabled,
      onClick,
      ...attrs
    },
    ref
  ) => {
    const variantClass = variant || "tertiary";

    return (
      <button
        ref={ref}
        className={cnb(
          "modernizedButton",
          `modernizedButton-${variantClass}`,
          `modernizedButton-${size}`,
          { "modernizedButton-block": block },
          className
        )}
        data-testid={testAttr}
        // eslint-disable-next-line react/button-has-type
        type={type}
        disabled={disabled}
        onClick={onClick}
        {...attrs}
      >
        {children}
      </button>
    );
  }
);

Button.propTypes = {
  /** Additional class(es) to add to the component. */
  className: PropTypes.string,
  /** The name of the test attribute to associate to the component. */
  testAttr: PropTypes.string,
  /** The markup node to insert into the button. */
  children: PropTypes.node.isRequired,
  /** The type of button that is rendered. */
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  /** The variant of the button. */
  variant: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  /** The size of the button. */
  size: PropTypes.oneOf(["sm", "md"]),
  /** Whether the button should display as a block level element. */
  block: PropTypes.bool,
  /** Whether the button is disabled or not. */
  disabled: PropTypes.bool,
  /**
   * The handler to execute when the button is clicked.
   *
   * @param {SyntheticEvent} evt - The React `SyntheticEvent`.
   */
  onClick: PropTypes.func
};

Button.defaultProps = {
  testAttr: "button",
  type: "button",
  size: "md"
};

export default Button;
